import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Minus, Plus, Trash2, Truck, CreditCard, Loader2, Clock, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { COUNTRY_GROUPS } from '../data/countries';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const { items, removeItem, updateQuantity, totalPrice, totalItems, toggleStorageUpgrade, hasStorageUpgrade } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // paymentReady: null = not checked yet, true = Stripe configured, false = not configured / backend down
  const [paymentReady, setPaymentReady] = useState(null);

  const [form, setForm] = useState({
    email: '',
    name: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
  });

  // ── Gift / recipient state ──
  // "self" = buying for yourself (default, no recipient block shown).
  // "gift" = sending to someone else — reveals name/email/phone + optional
  // separate shipping address block. Recipient email is fulfilment-only and
  // is NEVER granted passcode-recovery access on the backend.
  const [giftMode, setGiftMode] = useState('self');
  const [recipient, setRecipient] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [recipientShipping, setRecipientShipping] = useState({
    name: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
  });

  // Promo code state — server-validated, applied to the order summary before payment.
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, type, value, label}
  const [promoLoading, setPromoLoading] = useState(false);

  // Privacy Policy consent — required before checkout can proceed.
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  // Terms & Conditions + Privacy Policy acceptance — required before checkout can proceed.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Digital-product no-refund acknowledgement — required only when the cart is digital-only
  // (e.g. Memory Vault). Mirrors the additional clause in /lp-terms.
  const [agreedToDigitalRefund, setAgreedToDigitalRefund] = useState(false);

  const applyPromoCode = async () => {
    const code = String(promoInput).trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/checkout/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid promo code');
      }
      const data = await res.json();
      setAppliedPromo({ code: data.code, type: data.type, value: data.value, label: data.label });
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(err.message || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoInput('');
    setPromoError('');
  };

  // Client-side discount calculation for display only (backend re-computes authoritatively).
  const subtotalAfterUpgrade = totalPrice;
  const discountAmount = appliedPromo
    ? (appliedPromo.type === 'percent'
        ? Math.min(subtotalAfterUpgrade, subtotalAfterUpgrade * (Number(appliedPromo.value) / 100))
        : Math.min(subtotalAfterUpgrade, Number(appliedPromo.value)))
    : 0;
  const productAfterDiscount = Math.max(subtotalAfterUpgrade - discountAmount, 0);

  // Shipping rate — fetched from backend so the frontend never duplicates the
  // country list. Defaults to 0 while loading; the useEffect below fetches the
  // authoritative rate for the current country + cart contents.
  const [shipping, setShipping] = useState({ amount: 0, label: 'Standard UK delivery', region: 'uk' });

  // Digital-only carts (e.g. standalone Memory Vault) skip shipping entirely.
  // Mirrors the backend's `_is_cart_digital_only` so the order summary stays
  // honest before the customer hits "Pay". Backend re-validates authoritatively.
  const DIGITAL_PRODUCT_IDS = React.useMemo(
    () => new Set(['memory-vault-standalone', 'memory-vault-addon']),
    []
  );
  const isCartDigitalOnly = items.length > 0 && items.every((i) => DIGITAL_PRODUCT_IDS.has(i.id));
  // Cart contains ANY digital product (vault standalone or add-on). Controls
  // visibility of the digital-storage consent checkbox below — which only
  // applies when a digital Sent2ment product is in the order.
  const hasDigitalProduct = items.some((i) => DIGITAL_PRODUCT_IDS.has(i.id));

  const finalTotal = productAfterDiscount + (isCartDigitalOnly ? 0 : Number(shipping.amount || 0));

  // Verify backend + Stripe are configured before allowing payment submission.
  // If health check fails, payment is disabled so we never attempt to charge without a working backend.
  useEffect(() => {
    let cancelled = false;
    const checkHealth = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/health/integrations`, { method: 'GET' });
        if (!res.ok) throw new Error(`health check ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const stripeOk = data?.checks?.stripe?.status === 'ok';
        setPaymentReady(Boolean(stripeOk));
      } catch (_err) {
        if (!cancelled) setPaymentReady(false);
      }
    };
    checkHealth();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch shipping rate whenever the customer changes their country or cart.
  // Debounced lightly so we don't fire on every keystroke.
  // Skipped entirely for digital-only carts.
  // When shipping directly to a recipient at a different address, the
  // *recipient's* country determines the rate.
  const isGift = giftMode === 'gift';
  const useRecipientAddress = isGift && shipToDifferent;
  const shippingCountry = useRecipientAddress
    ? String(recipientShipping.country || '').trim()
    : String(form.country || '').trim();

  useEffect(() => {
    if (isCartDigitalOnly) {
      setShipping({ amount: 0, label: 'Digital delivery — no shipping', region: 'digital' });
      return;
    }
    const country = shippingCountry;
    if (!country) return;
    // Pass the cart's product ids so the backend can honour price-inclusive
    // delivery rules (e.g. Letters That Last ships free to UK).
    const productIds = (items || []).map((i) => i.id).filter(Boolean).join(',');
    const t = setTimeout(async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const url = `${API_URL}/api/checkout/shipping-rate?country=${encodeURIComponent(country)}${productIds ? `&product_ids=${encodeURIComponent(productIds)}` : ''}`;
        const res = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        setShipping({ amount: Number(data.amount) || 0, label: String(data.label || 'Shipping'), region: String(data.region || '') });
      } catch (_err) {
        // Silently ignore — backend will recompute authoritatively at session creation.
      }
    }, 300);
    return () => clearTimeout(t);
  }, [shippingCountry, isCartDigitalOnly, items]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    // Fail safely if backend/Stripe isn't configured — never attempt payment.
    if (paymentReady === false) {
      setError('Payments are temporarily unavailable. Please try again shortly.');
      return;
    }
    if (paymentReady === null) {
      setError('Verifying payment system, please wait a moment…');
      return;
    }

    // Validate
    const required = isCartDigitalOnly
      ? ['email', 'name']
      : ['email', 'name', 'address', 'city', 'postcode', 'country'];
    for (const field of required) {
      if (!form[field].trim()) {
        setError(`Please fill in your ${field.replace('_', ' ')}`);
        return;
      }
    }

    // Gift validation (physical carts only — the gift block never renders for
    // digital-only checkouts, so this is a no-op there).
    if (!isCartDigitalOnly && isGift) {
      if (!recipient.name.trim()) {
        setError("Please enter the recipient's name.");
        return;
      }
      // Recipient email is REQUIRED for gift orders so the recipient can
      // recover the memory's passcode later if they ever forget it.
      const recipientEmailTrimmed = recipient.email.trim();
      const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!recipientEmailTrimmed || !emailRe.test(recipientEmailTrimmed)) {
        setError("Please enter a valid recipient email address.");
        return;
      }
      if (shipToDifferent) {
        for (const field of ['address', 'city', 'postcode', 'country']) {
          if (!recipientShipping[field].trim()) {
            setError(`Please fill in the recipient's ${field}`);
            return;
          }
        }
      }
    }

    if (hasDigitalProduct && !agreedToPrivacy) {
      setError('Please agree to the Privacy Policy to continue.');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms & Conditions and Privacy Policy to continue.');
      return;
    }

    if (isCartDigitalOnly && !agreedToDigitalRefund) {
      setError('Please acknowledge the digital product refund policy to continue.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      // Build a fully-serialised payload: strings only for text, numbers only for numerics.
      // Never pass cart items, form state, or DOM/React values directly — extract primitives.
      const safeItems = items.map(i => ({
        id: String(i.id),
        quantity: Number(i.quantity) || 1,
        options: Array.isArray(i.options)
          ? i.options.map(o => String(o)).filter(Boolean)
          : [],
      }));

      const payload = {
        items: safeItems,
        email: String(form.email).trim(),
        shipping: {
          name: String(form.name).trim(),
          address: String(form.address).trim(),
          city: String(form.city).trim(),
          postcode: String(form.postcode).trim(),
          country: String(form.country).trim(),
        },
        origin_url: String(window.location.origin),
        source: source ? String(source) : undefined,
        promo_code: appliedPromo ? String(appliedPromo.code) : undefined,
        // Gift/recipient fields — only sent when the buyer opted into
        // "Someone else" for a physical cart. Recipient email is
        // fulfilment-only and never authorises passcode recovery.
        is_gift: !isCartDigitalOnly && isGift,
        recipient_name: !isCartDigitalOnly && isGift ? String(recipient.name).trim() : undefined,
        recipient_email: !isCartDigitalOnly && isGift ? String(recipient.email).trim() : undefined,
        recipient_phone: !isCartDigitalOnly && isGift ? String(recipient.phone).trim() : undefined,
        shipping_recipient: (!isCartDigitalOnly && isGift && shipToDifferent)
          ? {
              name: String(recipientShipping.name || recipient.name).trim(),
              address: String(recipientShipping.address).trim(),
              city: String(recipientShipping.city).trim(),
              postcode: String(recipientShipping.postcode).trim(),
              country: String(recipientShipping.country).trim(),
            }
          : undefined,
      };

      const res = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Checkout failed');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitializing(false), 150);
    return () => clearTimeout(timer);
  }, []);

  if (initializing) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <div className="text-center"><p className="text-sm text-[#6B6259]">Loading checkout...</p></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7F5F2] pt-32 pb-20" data-testid="checkout-empty">
        <div className="max-w-lg mx-auto px-6 text-center">
          <h1 className="font-heading text-3xl text-[#0B1F3A] font-light mb-4">Your basket is empty</h1>
          <p className="text-sm text-[#6B6259] mb-8">Add some keepsakes before checking out.</p>
          <button
            onClick={() => navigate(source === 'ltl' ? '/letters-that-last' : '/the-timeless-collection')}
            className="text-sm tracking-[0.15em] uppercase text-[#C6A96B] hover:text-[#b89a5e] transition-colors"
            data-testid="checkout-browse-btn"
          >
            {source === 'ltl' ? 'Back to Letters That Last' : 'Browse Collection'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2] pt-28 pb-20" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Back button */}
        <button
          onClick={() => navigate(source === 'ltl' ? '/letters-that-last' : -1)}
          className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#6B6259] hover:text-[#C6A96B] transition-colors mb-10"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft size={14} /> Continue Shopping
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left: Shipping Form */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                {isCartDigitalOnly ? (
                  <Check className="text-[#C6A96B]" size={18} strokeWidth={1.5} />
                ) : (
                  <Truck className="text-[#C6A96B]" size={18} strokeWidth={1.5} />
                )}
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#0B1F3A] font-medium" data-testid="checkout-section-heading">
                  {isCartDigitalOnly ? 'Your Details' : 'Shipping Details'}
                </h2>
              </div>

              <form onSubmit={handleCheckout} className="space-y-5">
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => updateField('email', e.target.value)}
                    placeholder="your@email.com"
                    data-testid="checkout-email"
                    className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Full Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => updateField('name', e.target.value)}
                    placeholder="Jane Smith"
                    data-testid="checkout-name"
                    className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                  />
                </div>

                {!isCartDigitalOnly && (
                  <>
                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Address</label>
                      <input
                        type="text"
                        value={form.address}
                        onChange={e => updateField('address', e.target.value)}
                        placeholder="123 Keepsake Lane"
                        data-testid="checkout-address"
                        className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">City</label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={e => updateField('city', e.target.value)}
                          placeholder="London"
                          data-testid="checkout-city"
                          className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Postcode</label>
                        <input
                          type="text"
                          value={form.postcode}
                          onChange={e => updateField('postcode', e.target.value)}
                          placeholder="SW1A 1AA"
                          data-testid="checkout-postcode"
                          className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Country</label>
                      <select
                        value={form.country}
                        onChange={e => updateField('country', e.target.value)}
                        data-testid="checkout-country"
                        className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm transition-colors appearance-none cursor-pointer"
                        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236B6259' d='M5 6L0 0h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
                      >
                        {COUNTRY_GROUPS.map(group => (
                          <optgroup key={group.label} label={group.label}>
                            {group.countries.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    {/* Gift / recipient block — physical carts only */}
                    <div className="pt-2" data-testid="checkout-gift-block">
                      <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-3">Is this for you or someone else?</label>
                      <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Recipient">
                        <button
                          type="button"
                          onClick={() => setGiftMode('self')}
                          data-testid="checkout-gift-self"
                          aria-pressed={giftMode === 'self'}
                          className={`px-4 py-3 border text-xs tracking-[0.15em] uppercase transition-all ${
                            giftMode === 'self'
                              ? 'bg-[#C6A96B]/10 border-[#C6A96B]/40 text-[#0B1F3A]'
                              : 'bg-white border-[#C6A96B]/15 text-[#6B6259] hover:border-[#C6A96B]/30'
                          }`}
                        >
                          For me
                        </button>
                        <button
                          type="button"
                          onClick={() => setGiftMode('gift')}
                          data-testid="checkout-gift-someone"
                          aria-pressed={giftMode === 'gift'}
                          className={`px-4 py-3 border text-xs tracking-[0.15em] uppercase transition-all ${
                            giftMode === 'gift'
                              ? 'bg-[#C6A96B]/10 border-[#C6A96B]/40 text-[#0B1F3A]'
                              : 'bg-white border-[#C6A96B]/15 text-[#6B6259] hover:border-[#C6A96B]/30'
                          }`}
                        >
                          Someone else
                        </button>
                      </div>

                      {isGift && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35 }}
                          className="mt-5 space-y-4 border-l border-[#C6A96B]/25 pl-5"
                          data-testid="checkout-recipient-fields"
                        >
                          <div>
                            <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Recipient name</label>
                            <input
                              type="text"
                              value={recipient.name}
                              onChange={e => setRecipient(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Their full name"
                              data-testid="checkout-recipient-name"
                              className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Recipient email</label>
                            <input
                              type="text"
                              inputMode="email"
                              autoComplete="email"
                              value={recipient.email}
                              onChange={e => setRecipient(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="their@email.com"
                              data-testid="checkout-recipient-email"
                              className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                            />
                            <p className="text-[11px] italic text-[#6B6259]/80 mt-2" data-testid="checkout-recipient-email-helper">
                              Held privately so the recipient can recover their passcode later if they ever forget it.
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Recipient phone <span className="normal-case tracking-normal italic text-[#6B6259]/70">— optional</span></label>
                            <input
                              type="tel"
                              value={recipient.phone}
                              onChange={e => setRecipient(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="e.g. +44 7000 000000"
                              data-testid="checkout-recipient-phone"
                              className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                            />
                          </div>

                          <label className="flex items-start gap-3 cursor-pointer group pt-1" data-testid="checkout-ship-different-label">
                            <input
                              type="checkbox"
                              checked={shipToDifferent}
                              onChange={e => setShipToDifferent(e.target.checked)}
                              data-testid="checkout-ship-different-checkbox"
                              className="mt-1 h-4 w-4 shrink-0 accent-[#C6A96B] border-[#C6A96B]/30 cursor-pointer"
                            />
                            <span className="text-xs text-[#6B6259] leading-[1.6]">
                              Ship directly to the recipient at a different address
                            </span>
                          </label>

                          {shipToDifferent && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                              className="space-y-4 pt-2"
                              data-testid="checkout-recipient-address-fields"
                            >
                              <div>
                                <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Delivery address</label>
                                <input
                                  type="text"
                                  value={recipientShipping.address}
                                  onChange={e => setRecipientShipping(prev => ({ ...prev, address: e.target.value }))}
                                  placeholder="123 Keepsake Lane"
                                  data-testid="checkout-recipient-address"
                                  className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">City</label>
                                  <input
                                    type="text"
                                    value={recipientShipping.city}
                                    onChange={e => setRecipientShipping(prev => ({ ...prev, city: e.target.value }))}
                                    placeholder="London"
                                    data-testid="checkout-recipient-city"
                                    className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Postcode</label>
                                  <input
                                    type="text"
                                    value={recipientShipping.postcode}
                                    onChange={e => setRecipientShipping(prev => ({ ...prev, postcode: e.target.value }))}
                                    placeholder="SW1A 1AA"
                                    data-testid="checkout-recipient-postcode"
                                    className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Country</label>
                                <select
                                  value={recipientShipping.country}
                                  onChange={e => setRecipientShipping(prev => ({ ...prev, country: e.target.value }))}
                                  data-testid="checkout-recipient-country"
                                  className="w-full bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm transition-colors appearance-none cursor-pointer"
                                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236B6259' d='M5 6L0 0h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center' }}
                                >
                                  {COUNTRY_GROUPS.map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                      {group.countries.map(c => (
                                        <option key={c} value={c}>{c}</option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </div>
                              <p className="text-[11px] italic text-[#6B6259]/80" data-testid="checkout-recipient-shipping-note">
                                Shipping is calculated using the recipient&rsquo;s country.
                              </p>
                            </motion.div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </>
                )}

                {isCartDigitalOnly && (
                  <p className="text-xs italic text-[#6B6259] leading-relaxed pt-1" data-testid="checkout-digital-note">
                    Your Memory Vault is delivered digitally. We'll send everything you need to your email.
                  </p>
                )}

                {/* Promo code */}
                <div className="pt-2">
                  <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Promo code</label>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between bg-[#C6A96B]/10 border border-[#C6A96B]/25 px-4 py-3" data-testid="checkout-promo-applied">
                      <span className="text-sm text-[#0B1F3A]">
                        <span className="font-medium tracking-wide">{appliedPromo.code}</span>
                        <span className="text-[#6B6259] ml-2">— {appliedPromo.label || 'applied'}</span>
                      </span>
                      <button
                        type="button"
                        onClick={removePromoCode}
                        data-testid="checkout-promo-remove"
                        className="text-xs tracking-[0.15em] uppercase text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoInput}
                        onChange={e => { setPromoInput(e.target.value); setPromoError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyPromoCode(); } }}
                        placeholder="Enter code"
                        data-testid="checkout-promo-input"
                        className="flex-1 bg-white border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3.5 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors tracking-wide uppercase"
                      />
                      <button
                        type="button"
                        onClick={applyPromoCode}
                        disabled={promoLoading || !promoInput.trim()}
                        data-testid="checkout-promo-apply"
                        className="px-6 border border-[#C6A96B]/40 text-[#0B1F3A] text-xs tracking-[0.2em] uppercase hover:bg-[#C6A96B]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {promoLoading ? '…' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {promoError && (
                    <p className="text-red-500 text-xs mt-2" data-testid="checkout-promo-error">{promoError}</p>
                  )}
                </div>

                {error && (
                  <p className="text-red-500 text-sm" data-testid="checkout-error">{error}</p>
                )}

                {hasDigitalProduct && (
                <div className="pt-2" data-testid="checkout-digital-consent-block">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="checkout-privacy-label">
                    <input
                      type="checkbox"
                      checked={agreedToPrivacy}
                      onChange={e => setAgreedToPrivacy(e.target.checked)}
                      required
                      data-testid="checkout-privacy-checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#C6A96B] border-[#C6A96B]/30 cursor-pointer"
                    />
                    <span className="text-xs text-[#6B6259] leading-[1.6]">
                      I consent to Sent2ment Ltd processing and securely storing my message, images, audio, or video content for the purpose of creating and delivering my Sent2ment memory. I understand I can request access or deletion of my data at any time.
                    </span>
                  </label>
                  <p className="text-[11px] text-[#6B6259]/80 mt-2 ml-7" data-testid="checkout-privacy-helper">
                    Includes secure storage for your message (10&ndash;25 years depending on selection).
                  </p>
                  <p className="text-[11px] italic text-[#6B6259]/80 mt-1 ml-7" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="checkout-privacy-reassurance">
                    Your memories are handled with care, privacy, and respect.
                  </p>
                </div>
                )}

                <div className="pt-1">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="checkout-terms-label">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={e => setAgreedToTerms(e.target.checked)}
                      required
                      data-testid="checkout-terms-checkbox"
                      className="mt-1 h-4 w-4 shrink-0 accent-[#C6A96B] border-[#C6A96B]/30 cursor-pointer"
                    />
                    <span className="text-xs text-[#6B6259] leading-[1.6]">
                      I have read and agree to the{' '}
                      <a
                        href="/lp-terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        data-testid="checkout-terms-link"
                        className="text-[#C6A96B] hover:text-[#b89a5e] underline underline-offset-2 transition-colors"
                      >
                        Terms &amp; Conditions
                      </a>
                      {' '}and{' '}
                      <a
                        href="/lp-privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        data-testid="checkout-terms-privacy-link"
                        className="text-[#C6A96B] hover:text-[#b89a5e] underline underline-offset-2 transition-colors"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                </div>

                {isCartDigitalOnly && (
                  <div className="pt-1" data-testid="checkout-digital-refund-block">
                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="checkout-digital-refund-label">
                      <input
                        type="checkbox"
                        checked={agreedToDigitalRefund}
                        onChange={e => setAgreedToDigitalRefund(e.target.checked)}
                        required
                        data-testid="checkout-digital-refund-checkbox"
                        className="mt-1 h-4 w-4 shrink-0 accent-[#C6A96B] border-[#C6A96B]/30 cursor-pointer"
                      />
                      <span className="text-xs text-[#6B6259] leading-[1.6]">
                        I understand this is a personalised digital product and refunds are not available once my Memory Vault has been created or accessed.
                      </span>
                    </label>
                  </div>
                )}

                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={loading || paymentReady !== true || (hasDigitalProduct && !agreedToPrivacy) || !agreedToTerms || (isCartDigitalOnly && !agreedToDigitalRefund)}
                    whileHover={loading ? {} : { scale: 1.01 }}
                    whileTap={loading ? {} : { scale: 0.99 }}
                    data-testid="checkout-pay-btn"
                    className="w-full bg-[#C6A96B] text-white font-medium tracking-[0.2em] uppercase text-xs py-5 transition-all duration-300 hover:bg-[#b89a5e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={14} /> Processing...
                      </>
                    ) : finalTotal <= 0 ? (
                      <>
                        <Check size={14} /> Complete Order (Free)
                      </>
                    ) : (
                      <>
                        <CreditCard size={14} /> Pay £{finalTotal.toFixed(2)}
                      </>
                    )}
                  </motion.button>
                  <p className="text-[10px] text-[#6B6259]/65 text-center mt-3 tracking-wide">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </form>
            </motion.div>
          </div>

          {/* Right: Order Summary */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white border border-[#C6A96B]/10 p-6"
            >
              <h2 className="text-sm tracking-[0.2em] uppercase text-[#0B1F3A] font-medium mb-6" data-testid="order-summary-title">
                Order Summary ({totalItems})
              </h2>

              <div className="space-y-4 mb-6">
                {items.filter(i => i.id !== 'storage-upgrade-25yr').map(item => (
                  <div key={item.id} className="flex gap-3" data-testid={`checkout-item-${item.id}`}>
                    {item.image && (
                      <div className="w-14 h-14 shrink-0 bg-[#F7F5F2] border border-[#C6A96B]/6 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] tracking-[0.12em] uppercase text-[#C6A96B]">{item.collection}</p>
                      <p className="text-sm text-[#0B1F3A] leading-tight">{item.name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-[#C6A96B]/12">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="px-2 py-1 text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid={`checkout-qty-minus-${item.id}`}>
                            <Minus size={10} />
                          </button>
                          <span className="px-2 py-1 text-xs text-[#0B1F3A] min-w-[20px] text-center">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-2 py-1 text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid={`checkout-qty-plus-${item.id}`}>
                            <Plus size={10} />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[#0B1F3A]">£{(item.price * item.quantity).toFixed(2)}</span>
                          <button onClick={() => removeItem(item.id)} className="text-[#6B6259]/55 hover:text-red-400 transition-colors" data-testid={`checkout-remove-${item.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Storage Upgrade */}
              {items.some(i => i.id !== 'storage-upgrade-25yr') && (
                <div className="border-t border-[#C6A96B]/10 pt-4 mb-4" data-testid="checkout-storage-upgrade">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Clock className="text-[#C6A96B]" size={12} strokeWidth={1.5} />
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#C6A96B] font-medium">Voice Storage</p>
                  </div>
                  <p className="text-xs text-[#6B6259] leading-[1.6] mb-2.5">10 years included as standard.</p>
                  <button
                    onClick={toggleStorageUpgrade}
                    data-testid="checkout-storage-toggle"
                    className={`w-full flex items-center gap-2.5 p-3 border transition-all duration-300 text-left ${
                      hasStorageUpgrade
                        ? 'bg-[#C6A96B]/5 border-[#C6A96B]/30'
                        : 'bg-[#F7F5F2] border-[#C6A96B]/12 hover:border-[#C6A96B]/25'
                    }`}
                  >
                    <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 transition-all duration-300 ${
                      hasStorageUpgrade ? 'bg-[#C6A96B] border-[#C6A96B]' : 'border-[#C6A96B]/30'
                    }`}>
                      {hasStorageUpgrade && <Check size={9} className="text-white" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-[#0B1F3A]">Extend to 25 years</p>
                    </div>
                    <p className="text-xs font-light text-[#0B1F3A] shrink-0">+ £9.99</p>
                  </button>
                </div>
              )}

              <div className="border-t border-[#C6A96B]/10 pt-4 space-y-2">
                <div className="flex justify-between text-xs text-[#6B6259]">
                  <span>Subtotal</span>
                  <span>£{totalPrice.toFixed(2)}</span>
                </div>
                {appliedPromo && (
                  <div className="flex justify-between text-xs text-[#C6A96B]" data-testid="checkout-summary-discount">
                    <span>Discount ({appliedPromo.code})</span>
                    <span>− £{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-[#6B6259]" data-testid="checkout-summary-shipping">
                  {isCartDigitalOnly ? (
                    <>
                      <span className="italic">Digital delivery</span>
                      <span className="italic">No shipping</span>
                    </>
                  ) : Number(shipping.amount || 0) === 0 ? (
                    <>
                      <span>{shipping.label || 'Standard UK delivery'}</span>
                      <span className="italic">Included</span>
                    </>
                  ) : (
                    <>
                      <span>{shipping.label || 'Shipping'}</span>
                      <span>£{Number(shipping.amount || 0).toFixed(2)}</span>
                    </>
                  )}
                </div>
                {!isCartDigitalOnly && (
                  <p className="text-[10px] italic text-[#6B6259]/75 leading-[1.55]" data-testid="checkout-delivery-eta" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '12px' }}>
                    Our delivery times are 7&ndash;10 days, however in busy periods it may take a little longer as every keepsake is made to order with precision and care.
                  </p>
                )}
                <div className="flex justify-between pt-2 border-t border-[#C6A96B]/8">
                  <span className="text-sm font-medium text-[#0B1F3A]">Total</span>
                  <span className="text-lg font-light font-heading text-[#0B1F3A]" data-testid="checkout-total">£{finalTotal.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-[#6B6259]/60 leading-[1.55] pt-2" data-testid="checkout-upload-reassurance">
                  Your voice or image can be uploaded after purchase using a secure link. You can return anytime when you're ready.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
